// File: src/config/constants.js

export const SITE_NAME = 'GreenScience Technology';
export const SITE_TAGLINE = 'Transforming Waste into Sustainable Futures';

export const NAV_LINKS = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About Us' },
  { path: '/solution', label: 'Our Solution' },
  { path: '/opportunity', label: 'Opportunity' },
  { path: '/impact', label: 'Impact' },
  { path: '/contact', label: "Let's Talk" },
];

export const SOCIAL_LINKS = {
  linkedin: 'https://www.linkedin.com/company/csr--vermicast-industries-inc/?viewAsMember=true',
};

// ─────────────────────────────────────────────────────────────────────────────
// Asset CDN
// Base URL for all media assets served via CloudFront.
// ─────────────────────────────────────────────────────────────────────────────
export const ASSETS_BASE_URL = 'https://assets.greenscience.ca';

export const TEAM_MEMBERS = [
  {
    id: 1,
    name: 'Kai',
    role: 'Driving the vision for a waste-free future',
    image: '/images/team-placeholder-1.jpg',
  },
  {
    id: 2,
    name: 'Team Member',
    role: 'Innovating sustainable waste solutions',
    image: '/images/team-placeholder-2.jpg',
  },
  {
    id: 3,
    name: 'Team Member',
    role: 'Scaling green technology worldwide',
    image: '/images/team-placeholder-3.jpg',
  },
];

export const VALUE_PROPS = [
  {
    id: 1,
    icon: 'patent',
    title: 'Patented Technology',
    description: 'Proprietary process protected by international patents.',
  },
  {
    id: 2,
    icon: 'zero-waste',
    title: 'Zero Waste to Landfill',
    description: 'Complete diversion of organic waste from landfills.',
  },
  {
    id: 3,
    icon: 'scalable',
    title: 'Scalable Solutions',
    description: 'Modular systems that grow with your needs.',
  },
  {
    id: 4,
    icon: 'revenue',
    title: 'New Revenue Streams',
    description: 'Turn waste liabilities into valuable resources.',
  },
];

export const PROBLEMS = [
  {
    id: 1,
    icon: 'landfill',
    title: 'Organic Waste in Landfills',
    description: 'Millions of tonnes of organic waste are buried in landfills each year, taking up space and contaminating soil and groundwater.',
  },
  {
    id: 2,
    icon: 'emissions',
    title: 'Greenhouse Gas Emissions',
    description: 'Decomposing organic waste produces methane—a greenhouse gas 80× more potent than CO₂ over 20 years.',
  },
  {
    id: 3,
    icon: 'cost',
    title: 'Wasted Revenue Streams',
    description: 'Valuable organic materials are discarded instead of being converted into renewable energy and useful byproducts.',
  },
];

export const SOLUTIONS = [
  {
    id: 1,
    icon: 'reduce-cost',
    title: 'Reduce Costs',
    description: 'Lower waste management expenses through efficient processing and resource recovery.',
  },
  {
    id: 2,
    icon: 'sustainability',
    title: 'Meet Sustainability Goals',
    description: 'Achieve ESG targets and regulatory compliance with proven zero-waste technology.',
  },
  {
    id: 3,
    icon: 'circular',
    title: 'Close the Loop',
    description: 'Transform waste into renewable outputs that feed back into the economy.',
  },
];

export const PROCESS_STEPS = [
  {
    id: 1,
    label: 'Waste In',
    description: 'Organic waste is collected and fed into our processing system.',
  },
  {
    id: 2,
    label: 'Processing',
    description: 'Our patented technology breaks down waste through an advanced conversion process.',
  },
  {
    id: 3,
    label: 'Renewable Output',
    description: 'Clean energy and valuable byproducts are generated—zero waste to landfill.',
  },
];

export const IMPACT_STATS = [
  {
    id: 1,
    value: '500K',
    suffix: '+',
    unit: 'Tonnes',
    label: 'Waste Diverted Annually',
  },
  {
    id: 2,
    value: '90',
    suffix: '%',
    unit: '',
    label: 'Reduction in GHG Emissions',
  },
  {
    id: 3,
    value: '100',
    suffix: '%',
    unit: '',
    label: 'Landfill Diversion Rate',
  },
];

export const MARKET_STATS = [
  {
    id: 1,
    value: '$37.7B',
    label: 'Global Organic Waste Management Market by 2034',
  },
  {
    id: 2,
    value: '7.91%',
    label: 'Annual Market Growth Rate (CAGR) through 2034',
  },
  {
    id: 3,
    value: '$1.9B',
    label: "Canada's Organic Recycling Market Projected by 2028",
  },
];

export const CONTACT_TYPES = [
  { id: 'info', label: 'Request for Info' },
  { id: 'partner', label: 'Become a Partner' },
  { id: 'other', label: 'Other' },
];

// Search index — client-side search across site content
export const SEARCH_INDEX = [
  {
    title: 'Home',
    path: '/',
    keywords: 'home landing hero transforming waste sustainable futures',
    description: 'GreenScience Technology — Transforming Waste into Sustainable Futures.',
  },
  {
    title: 'About Us — Mission',
    path: '/about',
    keywords: 'about mission pioneering waste-free world team timeline technology patented',
    description: 'Pioneering a Waste-Free World. Our patented technology transforms organic waste into clean energy.',
  },
  {
    title: 'About Us — Our Team',
    path: '/about',
    keywords: 'team people kai members sustainability innovation',
    description: 'The people behind GreenScience — passionate about sustainability, driven by innovation.',
  },
  {
    title: 'Our Solution — The Problem',
    path: '/solution',
    keywords: 'problem landfill organic waste greenhouse gas emissions methane revenue',
    description: 'Organic waste in landfills, greenhouse gas emissions, and wasted revenue streams.',
  },
  {
    title: 'Our Solution — How It Works',
    path: '/solution',
    keywords: 'solution how it works waste processing renewable output reduce costs sustainability circular',
    description: 'Reduce costs, meet sustainability goals, and close the loop with our patented process.',
  },
  {
    title: 'Impact — Environmental Stats',
    path: '/impact',
    keywords: 'impact environmental stats 500K tonnes diverted GHG emissions landfill diversion',
    description: '500K+ tonnes diverted annually, 90% reduction in GHG emissions.',
  },
  {
    title: 'Impact — Market Opportunity',
    path: '/impact',
    keywords: 'market opportunity $52B global organic waste management growth CAGR',
    description: '$52B global market by 2030, 6.4% annual growth rate.',
  },
  {
    title: 'Impact — Sustainability Roadmap',
    path: '/impact',
    keywords: 'sustainability roadmap 2030 target 1M tonnes milestones goals',
    description: 'Our path to diverting 1 million tonnes of organic waste from landfills by 2030.',
  },
  {
    title: 'Opportunity — Market Analysis',
    path: '/opportunity',
    keywords: 'opportunity market organic waste fertilizer $37 billion CAGR growth Canada hidden costs agricultural practices',
    description: 'Two rapidly expanding markets — organic waste management and organic fertilizer — and how GreenScience captures both.',
  },
  {
    title: "Let's Talk — Contact",
    path: '/contact',
    keywords: 'contact lets talk request info partner subscribe email',
    description: 'Get in touch — request information, become a partner, or subscribe for updates.',
  },
];

export const VIMEO_DEMO_VIDEO_ID = '49859260';